import { useEffect, useState } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { Mail } from 'lucide-react';

import { AuthShell } from './AuthShell';
import { supabase } from '../../lib/supabase';
import styles from './auth.module.css';

const COOLDOWN_S = 60;

interface LocationState {
  email?: string;
}

export function VerifyPendingScreen() {
  const location = useLocation();
  const state = location.state as LocationState | null;
  const email = state?.email;

  const [cooldown, setCooldown] = useState(0);
  const [resendOk, setResendOk] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => { setCooldown((s) => s - 1); }, 1000);
    return () => { clearTimeout(t); };
  }, [cooldown]);

  if (!email) {
    return <Navigate to="/auth/signup" replace />;
  }

  const onResend = async () => {
    setResendError(null);
    setResendOk(false);
    setResending(true);
    const { error } = await supabase.auth.resend({ type: 'signup', email });
    setResending(false);
    if (error) {
      setResendError('לא הצלחנו לשלוח שוב — נסה עוד דקה');
      return;
    }
    setResendOk(true);
    setCooldown(COOLDOWN_S);
  };

  return (
    <AuthShell
      title="בדוק את המייל"
      subtitle="שלחנו לך קישור אימות"
      brandSide={{
        title: 'עוד שלב אחד',
        body: 'אישור באימייל מבטיח שאף אחד אחר לא ירשם בשם שלך.',
      }}
    >
      <div className={styles.verifyIcon}>
        <Mail size={36} />
      </div>

      <p className={styles.sub}>
        שלחנו קישור אימות ל-<strong className="ltr-text">{email}</strong>.
        <br />
        הקישור תקף 24 שעות.
      </p>

      {resendOk ? (
        <div className={styles.success}>
          מייל נשלח שוב ל-<strong className="ltr-text">{email}</strong>
        </div>
      ) : null}

      {resendError ? <p className={styles.formError}>{resendError}</p> : null}

      <p className={styles.sub}>
        לא קיבלת? בדוק את האינבוקס וגם ספאם, או
      </p>

      <button
        type="button"
        className={styles.primary}
        onClick={() => { void onResend(); }}
        disabled={resending || cooldown > 0}
      >
        {cooldown > 0 ? (
          <>
            שלח שוב בעוד <span className="num">{String(cooldown)}</span> שנ׳
          </>
        ) : (
          'שלח שוב'
        )}
      </button>

      <p className={styles.switch}>
        אימייל לא נכון?{' '}
        <Link to="/auth/signup" className={styles.link}>
          חזור והתחל מחדש
        </Link>
      </p>
    </AuthShell>
  );
}
